import { useRef, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BodyMedium, BodyXS, Box, Button, Grid, H6, Select, SelectChangeEvent, Skeleton, Snackbar, TextField } from '@parspec/pixel';

import { validateEmail } from '../../../UserManagement/utils/utils';
import { USA_STATES, CANADA_STATES, someThingWentWrongMsg, DUPLICATE_ERP_ERROR_MSG, DUPLICATE_ERP_ERROR_CODE } from 'src/features/shared/constants';
import { trimEmptySpacesHandler, removeAllSpacesHandler, countryOptions, USA, CANADA, modifiedStateList } from '../../utils/utils';
import { sanitizePhoneNumber } from '../../utils/utils';
import { useLocationDetails } from '../context'
import { initialBranchDetailsErrorState } from '../../utils/helper';
import { attachmentDocumentArrFormatter } from '../../utils/utils';
import { useAddNewBranchDetailsMutation, useEditBranchDetailsMutation } from '../../queries';
import { useParspecNavigation } from 'src/app/Context';
import { ADD_LOCATION_SUCCESS_MSG, DUPLICATE_LOCATION_SUCCESS_MSG, EDIT_SETTINGS_SUCCESS_MSG } from '../../shared/constants';
import { CreateBranchResponse } from '../../queries/apiTypes';
import { NO_ROUNDING } from 'src/features/BOM/shared/constants';

const ADD = 'add'

export const BranchDetails = () => {
    const {
        currentLocationId,
        isDuplicate,
        companyDetailsLoading,
        branchDetailsTemplate,
        setBranchDetailsTemplate,
        branchDetailsErrorState,
        setBranchDetailsErrorState,
        branchBrandingTemplate,
        submittalTemplate,
        omPackageTemplate,
        quoteTemplate,
        branchLocationsRef,
        bomSettingTemplate
    } = useLocationDetails();

    const location = useLocation()
    const { pNavigate, hasUnsavedChangesRef } = useParspecNavigation();
    const [stateListOptions, setStateListOptions] = useState<
        {
            label: string;
            value: string;
        }[]
    >([{ label: '', value: '' }]);

    const currentPath = useMemo(() => {
        const tempArr = location.pathname.split('/')
        return tempArr[tempArr.length - 1]
    }, [location])

    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        const currentBranchLocations = branchLocationsRef.current;
        return () => {
            // This setter is coming from context, hence required to reset it
            setBranchDetailsErrorState(() => initialBranchDetailsErrorState);
            if (currentBranchLocations?.branchDetailsTemplate) {
                setBranchDetailsTemplate(currentBranchLocations.branchDetailsTemplate)
            }
        }
    }, [branchLocationsRef, setBranchDetailsErrorState, setBranchDetailsTemplate])

    useEffect(() => {
        if (branchDetailsTemplate.country && branchDetailsTemplate.country.toUpperCase() === USA) {
            setStateListOptions(modifiedStateList(USA_STATES));
        } else if (branchDetailsTemplate.country === CANADA) {
            setStateListOptions(modifiedStateList(CANADA_STATES));
        }
    }, [branchDetailsTemplate])

    // same api is used for both add and duplicate Locations
    const { mutateAsync: addNewBranchLocationData, isLoading: addNewLocationLoading } = useAddNewBranchDetailsMutation();
    const { mutateAsync: editBranchDetailsData, isLoading: isUpdateBranchDetailsLoading } = useEditBranchDetailsMutation();
    const [snackbarData, setSnackbarData] = useState('');

    const keydownHandler = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (formRef.current) {
                formRef.current.requestSubmit();
            }
        }
    };

    const requiredFieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        if (currentPath !== ADD) {
            hasUnsavedChangesRef.current = true;
        }
        const fieldName = event.target?.name;
        const fieldValue = event.target?.value as string;

        if (!Object.prototype.hasOwnProperty.call(branchDetailsErrorState, fieldName)) {
            return;
        }

        if (fieldName === 'country') {
            setBranchDetailsTemplate(() => ({
                ...branchDetailsTemplate,
                country: fieldValue,
                state: ''
            }));
            setBranchDetailsErrorState(() => ({
                ...branchDetailsErrorState,
                country: { ...branchDetailsErrorState.country, isError: false, title: '' },
                state: { ...branchDetailsErrorState.state, isError: true, title: 'Required' }
            }));

            let stateOptions: { label: string; value: string }[] = [];

            if (fieldValue === USA) {
                stateOptions = modifiedStateList(USA_STATES);
            } else if (fieldValue === CANADA) {
                stateOptions = modifiedStateList(CANADA_STATES);
            }
            return setStateListOptions(stateOptions);
        }

        if (branchDetailsErrorState[fieldName].isError) {
            setBranchDetailsErrorState({
                ...branchDetailsErrorState,
                [fieldName]: { ...branchDetailsErrorState[fieldName], isError: false, title: '' }
            });
        }
        if (fieldValue === '') {
            setBranchDetailsErrorState({
                ...branchDetailsErrorState,
                [fieldName]: { ...branchDetailsErrorState[fieldName], isError: true, title: 'Required' }
            });
        }

        if (fieldName === 'zipcode') {
            if (fieldValue.trim().length > 10) {
                setBranchDetailsErrorState({
                    ...branchDetailsErrorState,
                    zipcode: { ...branchDetailsErrorState.zipcode, isError: true, title: 'Max length allowed is 10' }
                });
            }
            return setBranchDetailsTemplate(() => ({ ...branchDetailsTemplate, zipcode: fieldValue }));
        }

        setBranchDetailsTemplate(() => ({ ...branchDetailsTemplate, [fieldName]: fieldValue }));
    };

    const unRequiredFieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (currentPath !== ADD) {
            hasUnsavedChangesRef.current = true;
        }
        const fieldName = event.target?.name;
        const fieldValue = event.target?.value;

        if (branchDetailsErrorState[fieldName].isError) {
            setBranchDetailsErrorState({
                ...branchDetailsErrorState,
                [fieldName]: { ...branchDetailsErrorState[fieldName], isError: false, title: '' }
            });
        }

        if (fieldValue.trim().length === 0) {
            return setBranchDetailsTemplate(() => ({ ...branchDetailsTemplate, [fieldName]: '' }));
        }

        if (fieldName === 'phone1') {
            const newFieldValue = sanitizePhoneNumber(event.target.value);
            return setBranchDetailsTemplate(() => ({
                ...branchDetailsTemplate,
                phone1: newFieldValue
            }));
        }

        setBranchDetailsTemplate(() => ({ ...branchDetailsTemplate, [fieldName]: fieldValue }));
    };

    const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const fieldName = event.target?.name;
        const eventType = event.type;

        if (!Object.prototype.hasOwnProperty.call(branchDetailsTemplate, fieldName)) {
            return;
        }

        if (branchDetailsTemplate[fieldName].length > 0 && eventType === 'blur') {
            if (fieldName === 'email') {
                if (!validateEmail(branchDetailsTemplate.email)) {
                    return setBranchDetailsErrorState({
                        ...branchDetailsErrorState,
                        email: { ...branchDetailsErrorState.email, isError: true, title: 'Please provide a valid email address' }
                    });
                }
            }

            if (fieldName === 'address2') {
                const trimmedValue = trimEmptySpacesHandler(branchDetailsTemplate[fieldName]);
                return setBranchDetailsTemplate(() => ({ ...branchDetailsTemplate, [fieldName]: trimmedValue }));
            }

            if (fieldName === 'website') {
                const trimmedValue = removeAllSpacesHandler(branchDetailsTemplate[fieldName]);
                return setBranchDetailsTemplate(() => ({ ...branchDetailsTemplate, [fieldName]: trimmedValue }));
            }

            if (branchDetailsTemplate[fieldName].trim().length === 0) {
                return setBranchDetailsErrorState({ ...branchDetailsErrorState, [fieldName]: { ...branchDetailsErrorState[fieldName], isError: true, title: 'Required' } });
            }

            const trimmedValue = trimEmptySpacesHandler(branchDetailsTemplate[fieldName]);
            setBranchDetailsTemplate(() => ({ ...branchDetailsTemplate, [fieldName]: trimmedValue }));

            if (fieldName === 'zipcode') {
                if (trimmedValue.length <= 10) {
                    return setBranchDetailsErrorState({ ...branchDetailsErrorState, zipcode: { ...branchDetailsErrorState.zipcode, isError: false, title: '' } });
                }
            }
        }
    };

    const resetDataHandler = () => {
        pNavigate(`/v2/settings/company-locations`);
    };

    const preparePaylodHandler = () => {
        const trimmedBranchName = trimEmptySpacesHandler(branchDetailsTemplate.name);
        const trimmedAddress1 = trimEmptySpacesHandler(branchDetailsTemplate.address1);
        const trimmedAddress2 = branchDetailsTemplate.address2 ? trimEmptySpacesHandler(branchDetailsTemplate.address2) : '';
        const trimmedWebsite = branchDetailsTemplate.website ? removeAllSpacesHandler(branchDetailsTemplate.website) : '';
        const trimmedcity = trimEmptySpacesHandler(branchDetailsTemplate.city);
        const trimmedZipcode = trimEmptySpacesHandler(branchDetailsTemplate.zipcode);
        const trimmedErpId = trimEmptySpacesHandler(branchDetailsTemplate.erpId);

        const branchNameFlag = trimmedBranchName.length === 0;
        const address1Flag = trimmedAddress1.length === 0;
        const cityFlag = trimmedcity.length === 0;
        const countryFlag = branchDetailsTemplate.country.length === 0;
        const stateFlag = branchDetailsTemplate.state.length === 0;
        const zipcodeFlag = branchDetailsTemplate.zipcode.trim().length === 0 || branchDetailsTemplate.zipcode.trim().length > 10;

        if (branchNameFlag || address1Flag || cityFlag || countryFlag || stateFlag || zipcodeFlag) {
            let zipCodeErrorMsg = '';
            if (branchDetailsTemplate.zipcode.trim().length === 0) {
                zipCodeErrorMsg = 'Required';
            }
            if (branchDetailsTemplate.zipcode.trim().length > 10) {
                zipCodeErrorMsg = 'Max length allowed is 10';
            }

            setBranchDetailsErrorState({
                ...branchDetailsErrorState,
                name: { ...branchDetailsErrorState.name, isError: branchNameFlag, title: `${branchNameFlag ? 'Required' : ''}` },
                address1: { ...branchDetailsErrorState.address1, isError: address1Flag, title: `${address1Flag ? 'Required' : ''}` },
                city: { ...branchDetailsErrorState.city, isError: cityFlag, title: `${cityFlag ? 'Required' : ''}` },
                country: { ...branchDetailsErrorState.country, isError: countryFlag, title: `${countryFlag ? 'Required' : ''}` },
                state: { ...branchDetailsErrorState.state, isError: stateFlag, title: `${stateFlag ? 'Required' : ''}` },
                zipcode: { ...branchDetailsErrorState.zipcode, isError: zipcodeFlag, title: zipCodeErrorMsg }
            });
            return false
        }

        if (branchDetailsTemplate?.email?.length > 0 && !validateEmail(branchDetailsTemplate?.email)) {
            setBranchDetailsErrorState({
                ...branchDetailsErrorState,
                email: { ...branchDetailsErrorState.email, isError: true, title: 'Please provide a valid email address' }
            });
            return false
        }

        const companyDetaisObj = {
            name: trimmedBranchName,
            address1: trimmedAddress1,
            address2: trimmedAddress2,
            website: trimmedWebsite,
            phone1: branchDetailsTemplate.phone1 || '',
            city: trimmedcity,
            state: branchDetailsTemplate.state,
            zipcode: trimmedZipcode,
            country: branchDetailsTemplate.country,
            email: branchDetailsTemplate.email || '',
            erp_id: trimmedErpId
        };
        return companyDetaisObj
    }

    const onAddSubmitHandler = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        hasUnsavedChangesRef.current = false;

        let companyDetaisObj = {}
        if (!preparePaylodHandler()) {
            return
        } else {
            companyDetaisObj = preparePaylodHandler()
        }

        const brandingData = { logo: branchBrandingTemplate?.logo?.url ? branchBrandingTemplate?.logo?.url : '', picture: branchBrandingTemplate?.picture?.url ? branchBrandingTemplate?.picture.url : '' }

        const payloadData = {
            company_details: { ...companyDetaisObj },
            company_branding: brandingData,
            submittal: { ...submittalTemplate },
            o_m: { ...omPackageTemplate },
            quote: { ...quoteTemplate, attachments: { ...quoteTemplate.attachments, documents: attachmentDocumentArrFormatter(quoteTemplate?.attachments?.documents) } },
            bom: { ...bomSettingTemplate, rounding_precision: bomSettingTemplate.rounding_behaviour === NO_ROUNDING ? NO_ROUNDING : bomSettingTemplate.rounding_precision }
        };

        const payload = { payload: payloadData }

        try {
            // same api is used for both add and duplicate Locations

            const resp = await addNewBranchLocationData({ data: payload });
            const createdCompanyId = (resp?.data as unknown as CreateBranchResponse)?.id

            if (isDuplicate) {
                setSnackbarData(() => DUPLICATE_LOCATION_SUCCESS_MSG)
            } else {
                setSnackbarData(() => ADD_LOCATION_SUCCESS_MSG)
            }
            setBranchDetailsErrorState(initialBranchDetailsErrorState); //reset after edit success
            pNavigate(`/v2/settings/company-locations/edit/${createdCompanyId}/branchDetails`);
        } catch (error: any) {
            if (error?.response?.data?.error_code === DUPLICATE_ERP_ERROR_CODE) {
                setBranchDetailsErrorState({ ...branchDetailsErrorState, erpId: { isError: true, title: DUPLICATE_ERP_ERROR_MSG } });
            } else {
                pNavigate('/v2/settings/company-locations');
            }
        }
    };

    const onEditSubmitHandler = async (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement>) => {

        event.preventDefault();

        let companyDetaisObj = {}
        if (!preparePaylodHandler()) {
            return
        } else {
            companyDetaisObj = preparePaylodHandler()
        }

        const payload = {
            apply_settings_to_company_ids: [currentLocationId], payload: companyDetaisObj
        }

        try {
            hasUnsavedChangesRef.current = false;
            await editBranchDetailsData({ data: payload });
            setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
            setBranchDetailsErrorState(initialBranchDetailsErrorState); //reset after edit success
        } catch (err: any) {
            if (err?.response?.data?.error_code === DUPLICATE_ERP_ERROR_CODE) {
                setBranchDetailsErrorState({ ...branchDetailsErrorState, erpId: { isError: true, title: DUPLICATE_ERP_ERROR_MSG } });
            } else {
                setSnackbarData(() => err?.response?.data?.message || someThingWentWrongMsg);
            }
        }
    }

    return (
        <Box>
            <Box display="flex">
                <Box>
                    <H6>Branch Details</H6>
                </Box>
                <Box ml="auto" display="flex">
                    <Box display="flex">
                        <Box mr={2}>
                            <Button variant="outlined" color="secondary" onClick={resetDataHandler}>
                                Cancel
                            </Button>
                        </Box>
                        <Box>
                            {currentLocationId && currentLocationId !== 0 && !isDuplicate ? (
                                <Button variant="contained" onClick={onEditSubmitHandler} isLoading={isUpdateBranchDetailsLoading} disabled={companyDetailsLoading}>
                                    Save
                                </Button>
                            ) : (
                                <Button variant="contained" onClick={onAddSubmitHandler} isLoading={addNewLocationLoading} disabled={companyDetailsLoading}>
                                    Save
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <form ref={formRef}>
                <Box pt={6}>
                    <Box display="flex " position="relative" height={'100%'} justifyContent={'space-between'}>
                        <Box width="50%" pr={6}>
                            <BodyMedium color="text.secondary" fontWeight={500} mb={8}>
                                Branch Information
                            </BodyMedium>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        required
                                        label="Branch Name"
                                        name="name"
                                        variant="outlined"
                                        value={branchDetailsTemplate.name}
                                        autoComplete="off"
                                        onBlur={onBlurHandler}
                                        onChange={requiredFieldChangeHandler}
                                        onKeyDown={keydownHandler}
                                        helperText={branchDetailsErrorState.name.title}
                                        error={branchDetailsErrorState.name.isError}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        label="Branch Primary Email"
                                        name="email"
                                        variant="outlined"
                                        value={branchDetailsTemplate.email}
                                        autoComplete="off"
                                        onBlur={onBlurHandler}
                                        onChange={unRequiredFieldChangeHandler}
                                        onKeyDown={keydownHandler}
                                        helperText={branchDetailsErrorState.email.title}
                                        error={branchDetailsErrorState.email.isError}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        label="Branch Primary Phone"
                                        name="phone1"
                                        variant="outlined"
                                        type={'tel'}
                                        value={branchDetailsTemplate.phone1}
                                        autoComplete="off"
                                        onChange={unRequiredFieldChangeHandler}
                                        onKeyDown={keydownHandler}
                                        helperText={branchDetailsErrorState.phone1.title}
                                        error={branchDetailsErrorState.phone1.isError}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        label="Branch Website"
                                        name="website"
                                        variant="outlined"
                                        value={branchDetailsTemplate.website}
                                        autoComplete="off"
                                        onChange={unRequiredFieldChangeHandler}
                                        onBlur={onBlurHandler}
                                        onKeyDown={keydownHandler}
                                        helperText={branchDetailsErrorState.website.title}
                                        error={branchDetailsErrorState.website.isError}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        label="ERP ID"
                                        name="erpId"
                                        variant="outlined"
                                        value={branchDetailsTemplate.erpId}
                                        autoComplete="off"
                                        onChange={unRequiredFieldChangeHandler}
                                        onBlur={onBlurHandler}
                                        onKeyDown={keydownHandler}
                                        helperText={branchDetailsErrorState.erpId.title}
                                        error={branchDetailsErrorState.erpId.isError}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                        </Box>
                        <Box width="50%">
                            <BodyMedium color="text.secondary" fontWeight={500} mb={8}>
                                Branch Address
                            </BodyMedium>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        required
                                        label="Address Line 1"
                                        name="address1"
                                        variant="outlined"
                                        value={branchDetailsTemplate.address1}
                                        autoComplete="off"
                                        onChange={requiredFieldChangeHandler}
                                        onBlur={onBlurHandler}
                                        onKeyDown={keydownHandler}
                                        helperText={branchDetailsErrorState.address1.title}
                                        error={branchDetailsErrorState.address1.isError}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        label="Address Line 2"
                                        name="address2"
                                        variant="outlined"
                                        value={branchDetailsTemplate.address2}
                                        autoComplete="off"
                                        onChange={unRequiredFieldChangeHandler}
                                        onBlur={onBlurHandler}
                                        onKeyDown={keydownHandler}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                            <Box height={'72px'}>
                                {!companyDetailsLoading ? (
                                    <TextField
                                        required
                                        label="City"
                                        name="city"
                                        variant="outlined"
                                        value={branchDetailsTemplate.city}
                                        autoComplete="off"
                                        onChange={requiredFieldChangeHandler}
                                        onBlur={onBlurHandler}
                                        onKeyDown={keydownHandler}
                                        helperText={branchDetailsErrorState.city.title}
                                        error={branchDetailsErrorState.city.isError}
                                    />
                                ) : (
                                    <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                            <Box height={'72px'}>
                                <Grid item md={6}>
                                    {!companyDetailsLoading && (
                                        <>
                                            <Select
                                                required
                                                label="Country*"
                                                name="country"
                                                id="country"
                                                labelId="country"
                                                optionLabelKeyname="label"
                                                optionValueKeyname="value"
                                                options={countryOptions}
                                                value={
                                                    branchDetailsTemplate.country === 'usa' || branchDetailsTemplate.country === 'Usa'
                                                        ? branchDetailsTemplate.country.toUpperCase()
                                                        : branchDetailsTemplate.country
                                                }
                                                onChange={requiredFieldChangeHandler}
                                                error={branchDetailsErrorState.country.isError}
                                            />
                                            {branchDetailsErrorState.country.isError && (
                                                <Box my={2}>
                                                    <BodyXS color={'error.main'}>{branchDetailsErrorState.country.title}</BodyXS>
                                                </Box>
                                            )}
                                        </>
                                    )}{' '}
                                    {companyDetailsLoading && <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />}
                                </Grid>
                            </Box>
                            <Box height={'72px'} display="flex " position="relative">
                                <Box width={'50%'} mr={6}>
                                    <Grid item md={6}>
                                        {!companyDetailsLoading && stateListOptions.length > 0 && (
                                            <>
                                                <Select
                                                    required
                                                    id="state"
                                                    labelId="state"
                                                    label="State / Province*"
                                                    name="state"
                                                    value={branchDetailsTemplate.state}
                                                    onChange={requiredFieldChangeHandler}
                                                    options={stateListOptions}
                                                    error={branchDetailsErrorState.state.isError}
                                                    optionLabelKeyname="label"
                                                    optionValueKeyname="value"
                                                />

                                                {branchDetailsErrorState.state.isError && (
                                                    <Box my={2}>
                                                        <BodyXS color={'error.main'}>{branchDetailsErrorState.state.title}</BodyXS>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                        {companyDetailsLoading && stateListOptions.length > 0 && <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />}
                                    </Grid>
                                </Box>
                                <Box width={'50%'}>
                                    {!companyDetailsLoading ? (
                                        <TextField
                                            required
                                            label="Zip Code"
                                            name="zipcode"
                                            variant="outlined"
                                            value={branchDetailsTemplate.zipcode}
                                            autoComplete="off"
                                            onChange={requiredFieldChangeHandler}
                                            onBlur={onBlurHandler}
                                            onKeyDown={keydownHandler}
                                            helperText={branchDetailsErrorState.zipcode.title}
                                            error={branchDetailsErrorState.zipcode.isError}
                                        />
                                    ) : (
                                        <Skeleton width="100%" height={36} variant="rectangular" sx={{ borderRadius: 1 }} />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </form>

            {<Snackbar open={snackbarData?.length > 0} message={snackbarData} onClose={() => setSnackbarData(() => '')} />}
        </Box>
    );
};
